import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";

import { appConstants } from "../../config/app-constants";

export type Props = {
	value: string;
	otpLength: number;
	onChange: (value: string) => void;
	error?: string;
	touched: boolean;
	handleBlur: (e: any) => void;
};

const OTPInput = (props: Props) => {
	const { value, otpLength, onChange, touched, handleBlur, error } = props;
	const [otpFields, setOtpFields] = useState<boolean[]>(Array(otpLength).fill(false));
	const firstInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (firstInputRef.current) {
			firstInputRef.current.focus();
		}
	}, []);

	useEffect(() => {
		const valueArray = value.split("");
		const newOtpFields = valueArray.map((char) => Boolean(char.trim()));
		setOtpFields(newOtpFields);
	}, [value]);

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const inputElement = e.target as HTMLInputElement;
		inputElement.blur();
		const pastedData = e.clipboardData.getData("text");
		const pastedValues = pastedData.split("").slice(0, otpLength);

		const newOtpFields = Array(otpLength).fill(false);
		pastedValues.forEach((char, idx) => {
			newOtpFields[idx] = true;
		});

		setOtpFields(newOtpFields);
		onChange(pastedValues.join(""));
	};

	const otpDigit = useMemo(() => {
		const valueArray = value.split("");
		const validOtpDigit: Array<string> = [];

		for (let i = 0; i < otpLength; i++) {
			const char = valueArray[i];

			if (appConstants.OTP_DIGIT.test(char)) {
				validOtpDigit.push(char);
			} else {
				validOtpDigit.push("");
			}
		}

		return validOtpDigit;
	}, [value, otpLength]);

	const focusToNextInput = (target: HTMLElement) => {
		const nextElementSibling = target.nextElementSibling as HTMLInputElement | null;

		if (nextElementSibling) {
			nextElementSibling.focus();
		}
	};

	const focusToPrevInput = (target: HTMLElement) => {
		const previousElementSibling = target.previousElementSibling as HTMLInputElement | null;

		if (previousElementSibling) {
			previousElementSibling.focus();
		}
	};

	const InputOnChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
		const target = e.target;
		let targetValue = target.value.trim();
		const isTargetValueDigit = appConstants.OTP_DIGIT.test(targetValue);

		if (!isTargetValueDigit && targetValue !== "") {
			return;
		}

		targetValue = isTargetValueDigit ? targetValue : " ";

		const targetValueLength = targetValue.length;

		if (targetValueLength === 1) {
			const newValue = value.substring(0, idx) + targetValue + value.substring(idx + 1);

			onChange(newValue);

			if (!isTargetValueDigit) {
				return;
			}

			focusToNextInput(target);

			const nextElementSibling = target.nextElementSibling as HTMLInputElement | null;

			if (nextElementSibling) {
				nextElementSibling.focus();
			}
		} else if (targetValueLength === otpLength) {
			onChange(targetValue);

			target.blur();
		}
	};

	const InputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		const targetValue = target.value;
		const { key } = e;

		if (key === "ArrowRight" || key === "ArrowDown") {
			e.preventDefault();
			return focusToNextInput(target);
		}

		if (key === "ArrowLeft" || key === "ArrowUp") {
			e.preventDefault();
			return focusToPrevInput(target);
		}

		// keep the selection range position
		// if the same digit was typed
		target.setSelectionRange(0, targetValue.length);

		if (e.key !== "Backspace" || target.value !== "") {
			return;
		}
		focusToPrevInput(target);

		const previousElementSibling = target.previousElementSibling as HTMLInputElement | null;

		if (previousElementSibling) {
			previousElementSibling.focus();
		}
	};

	const InputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		const { target } = e;
		target.setSelectionRange(0, target.value.length);
	};

	return (
		<Form.Group className="form-group" controlId="otp">
			<div className={classNames("otp-group", Boolean(error) && touched && "is-invalid")}>
				{otpDigit.map((digit, idx) => (
					<Form.Control
						type="text"
						autoComplete="one-time-code"
						name="otp"
						key={idx}
						inputMode="numeric"
						value={digit}
						onBlur={(e) => {
							handleBlur(e);
							const newOtpFields = [...otpFields];
							newOtpFields[idx] = true;
							setOtpFields(newOtpFields);
						}}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => InputOnChange(e, idx)}
						// isInvalid={(touched && error) || (digit === "" && touched)}
						isInvalid={!appConstants.OTP_DIGIT.test(digit) && touched}
						pattern="\d{1}"
						maxLength={otpLength}
						onKeyDown={InputOnKeyDown}
						onFocus={InputOnFocus}
						onPaste={handlePaste}
						ref={idx === 0 ? firstInputRef : null}
					/>
				))}
			</div>
			<Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
		</Form.Group>
	);
};

export default OTPInput;
